import AuthContextProvider from '@components/AuthContext'
import Cookies from '@components/Cookies'
import Debug from '@components/Debug'
import LocalizationProvider from '@components/Localization'
import { Messages } from '@components/Localization/messages/types'
import { NotificationsProvider } from '@components/NotificationsProvider'
import Tracking from '@components/Tracking'
import UrqlClientProvider from '@components/UrqlClientProvider'
import CheckoutProvider from '@components/_checkout/CheckoutProvider'
import WishlistProvider from '@components/WishlistProvider'
import { VERSION } from '@config/version'
import { theme } from '@gassan-ui'
import ErrorPage from '@pages/_error'
import * as Sentry from '@sentry/browser'
import { resetIdCounter } from 'downshift'
import { DefaultSeo } from 'next-seo'
import { AppContext, AppProps } from 'next/app'
import Head from 'next/head'
import React from 'react'
import { ThemeProvider } from 'styled-components'
import { Client } from 'urql'
import { TourBookingProvider } from '@components/_tours/TourBookingProvider'
import { useRouter } from 'next/router'
import TopLoadingBar from '@components/TopLoadingBar'
import { LoadCart } from '@components/LoadCart'
import { Fonts } from '@components/gassan-ui/Theme/Fonts'

Sentry.init({
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  release: `gassan@${VERSION}`,
  enabled: process.env.NODE_ENV === 'production'
})

type Props = {
  err?: Error
  lang: null | string
  messages: Messages
  client: Client
}

const CustomApp = ({ Component, pageProps, err, lang, messages }: AppProps & Props) => {
  const router = useRouter()
  // The first url part after the host, is dynamic ([lang])
  // because it's dynamic, it can also be gassan/non-existing, or gassan/non-existing/something-else.
  // we check if it exist (can also be null for the homepage), and if it does, check if it's either "nl" or "en"
  let shouldRender404 = false
  if (lang !== null && !['nl', 'en', 'sg'].includes(lang)) {
    shouldRender404 = true
  }

  // If router.asPath is /sg or includes /sg/ we only want to show url's that have /rolex
  // E.g. /sg/contact is invalid, /sg/rolex/day-date is valid
  if (
    (router.asPath === '/sg' || router.asPath.includes('/sg/')) &&
    !router.asPath.includes('/rolex')
  ) {
    shouldRender404 = true
  }

  return (
    <>
      <Debug />
      <Head>
        <meta
          key="viewport"
          name="viewport"
          content="width=device-width, initial-scale=1.0, viewport-fit=cover"
        />
        {['staging', 'production', 'development'].includes(process.env.NEXT_PUBLIC_STAGE) && (
          <>
            <script
              type="text/javascript"
              src="//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
              async
            />
            <script src="https://static.rolex.com/retailers/clock/retailercall.js" />
          </>
        )}
        <meta
          name="google-site-verification"
          content="bfGweToFCHLEBA35hao7JcbsV1MA3WL9uI8cFnjIDjQ"
        />
      </Head>
      <Fonts />
      <DefaultSeo />
      <Tracking />
      <ThemeProvider theme={theme}>
        <LocalizationProvider initialLang={lang || 'nl'} initialMessages={messages}>
          <UrqlClientProvider lang={lang ? lang : undefined}>
            <LoadCart />
            <CheckoutProvider>
              <AuthContextProvider>
                <NotificationsProvider>
                  <WishlistProvider>
                    <TourBookingProvider>
                      <TopLoadingBar />
                      <Cookies />
                      {shouldRender404 ? (
                        <ErrorPage statusCode={404} />
                      ) : (
                        <Component {...pageProps} err={err} lang={lang || 'nl'} />
                      )}
                    </TourBookingProvider>
                  </WishlistProvider>
                </NotificationsProvider>
              </AuthContextProvider>
            </CheckoutProvider>
          </UrqlClientProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </>
  )
}

CustomApp.getInitialProps = async ({ Component, ctx, router }: AppContext) => {
  let lang = (router.query.lang as string) || null

  if (router.route === '/404') {
    // On a 404 page we need to parse the lang from the url
    const errorLang = router.asPath.split('/').filter(t => t !== '')[0]
    // If errorLang = nl | en | sg return it as is (sg as en), so the error page is set to the correct lang
    if (['en', 'sg', 'nl'].includes(errorLang)) {
      lang = errorLang === 'sg' ? 'en' : errorLang
    }
  }

  let pageProps = {}
  // This makes lang available in every getInitialProps call
  // e.g Page.getInitialProps({lang})
  if (Component.getInitialProps) {
    // default lang nl, if we're on a page we're it's not available, nl will be used
    pageProps = await Component.getInitialProps({ ...ctx, lang: lang || 'nl' })
  }

  // Get initial messages
  const messages = await (
    await import(
      `../components/Localization/messages/${lang && lang === 'sg' ? 'en' : lang || 'nl'}`
    )
  ).default

  // Reset downshift id count
  if (typeof window === 'undefined') {
    resetIdCounter()
  }

  return {
    pageProps,
    messages,
    lang
  }
}

export default CustomApp
